// src/pages/EditItemPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getItemById, updateItem, getCategories, getLocations, createTransaction, deleteItem } from '../services/api';
import LabelPrintModal from '../components/LabelPrintModal';
import PhotoUpload from '../components/PhotoUpload';

const PINK_BG     = '#FFF0F0';
const PINK_BORDER = '#FFCCCC';
const PINK_HEADER = '#FFE0E0';
const PINK_WARN   = '#FF6B6B';

export default function EditItemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { worker, isAdmin } = useContext(AuthContext);

  const [item, setItem]               = useState(null);
  const [categories, setCategories]   = useState([]);
  const [locations, setLocations]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const [showNameConfirm, setShowNameConfirm]           = useState(false);
  const [pendingName, setPendingName]                   = useState('');
  const [showQtyAdjust, setShowQtyAdjust]               = useState(false);
  const [newQty, setNewQty]                             = useState('');
  const [qtyReason, setQtyReason]                       = useState('');
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showPrintLabel, setShowPrintLabel] = useState(false);

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [itemRes, catRes, locRes] = await Promise.all([
        getItemById(id),
        getCategories(),
        getLocations()
      ]);
      const i = itemRes.data.item;
      setItem(i);
      setCategories(catRes.data.categories || []);
      setLocations(locRes.data.locations || []);
      setForm({
        name:                 i.name || '',
        category_id:          i.category_id || '',
        model_number:         i.model_number || '',
        serial_number:        i.serial_number || '',
        purchase_price:       i.purchase_price || '',
        purchase_date:        i.purchase_date ? i.purchase_date.split('T')[0] : '',
        warranty_expiry:      i.warranty_expiry ? i.warranty_expiry.split('T')[0] : '',
        condition:            i.condition || '',
        battery_platform:     i.battery_platform || '',
        unit_of_measure:      i.unit_of_measure || '',
        reorder_threshold:    i.reorder_threshold || '',
        reorder_quantity:     i.reorder_quantity || '',
        home_location_id:     i.home_location_id || '',
        manufacturer_website: i.manufacturer_website || '',
        manufacturer_phone:   i.manufacturer_phone || '',
        notes:                i.notes || '',
      });
      setNewQty(parseFloat(i.available_quantity) || 0);
    } catch (err) {
      setError('Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleNameChange = (val) => {
    if (val !== item.name) {
      setPendingName(val);
      setShowNameConfirm(true);
    } else {
      set('name', val);
    }
  };

  const confirmNameChange = () => {
    set('name', pendingName);
    setShowNameConfirm(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const cleaned = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      await updateItem(id, cleaned);
      setSuccess('✅ Item updated successfully');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleQtyAdjust = async () => {
    if (newQty === '' || newQty < 0) { setError('Please enter a valid quantity'); return; }
    setSaving(true);
    setError('');
    try {
      await createTransaction({
        item_id:          id,
        transaction_type: 'adjust',
        performed_by:     worker.worker_id,
        quantity:         parseFloat(newQty),
        notes:            qtyReason || 'Admin quantity adjustment'
      });
      setSuccess(`✅ Quantity adjusted to ${newQty} ${item.unit_of_measure || 'units'}`);
      setShowQtyAdjust(false);
      setQtyReason('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Adjustment failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setSaving(true);
    setError('');
    try {
      await deleteItem(id);
      navigate('/search');
    } catch (err) {
      setError(err.response?.data?.error || 'Deactivation failed');
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="screen edit-admin-page" style={{ background: PINK_BG }}>
      <div className="loading"><div className="spinner" /><span>Loading...</span></div>
    </div>
  );

  if (!form) return null;

  const isBulk    = item?.item_type === 'bulk';
  const assetCats = categories.filter(c => c.item_type === 'asset' || c.item_type === 'both');
  const bulkCats  = categories.filter(c => c.item_type === 'bulk'  || c.item_type === 'both');

  return (
    <div className="screen edit-admin-page" style={{ background: PINK_BG }}>
      <style>{`
        .edit-admin-page input,
        .edit-admin-page select,
        .edit-admin-page textarea {
          color: #1A2C42 !important;
          background: white !important;
        }
      `}</style>

      {/* Header */}
      <div className="screen-header" style={{ background: '#CC0000', borderBottom: `3px solid ${PINK_WARN}` }}>
        <button onClick={() => navigate(`/item/${id}`)}
          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '4px 8px 4px 0' }}>
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: 'white', fontSize: '1rem' }}>⚠️ Admin Edit Mode</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>{item?.name}</p>
        </div>
      </div>

      {/* Warning banner */}
      <div style={{ background: PINK_WARN, color: 'white', padding: '10px 16px', fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>
        ⚠️ Admin Edit Mode — Changes are permanent and cannot be undone
      </div>

      <div className="screen-content" style={{ background: PINK_BG }}>

        {error   && <div className="alert alert-error"   style={{ marginBottom: 12 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: 12 }}>{success}</div>}

        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className={`badge badge-${item?.current_status}`}>{item?.current_status?.replace(/_/g, ' ')}</span>
          <span style={{ fontSize: '0.8rem', color: '#666' }}>{item?.asset_tag || item?.qr_code_id}</span>
          <button
            onClick={() => setShowPrintLabel(true)}
            style={{
              marginLeft: 'auto', fontSize: '0.78rem', padding: '5px 10px',
              background: '#1A2C42', color: 'white', border: 'none', borderRadius: 6,
              cursor: 'pointer', fontWeight: 600, flexShrink: 0
            }}>
            Print Label
          </button>
        </div>

        <Field label="Photo">
          <PhotoUpload
            compact
            currentUrl={item?.photo_url}
            onUpload={async (url) => {
              try {
                await updateItem(id, { photo_url: url });
                await loadData();
                setSuccess('✅ Photo updated');
              } catch {
                setError('Photo uploaded but could not be saved. Try again.');
              }
            }}
          />
        </Field>

        <Field label="Item Name">
          <input className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
            value={form.name}
            onChange={e => set('name', e.target.value)}
            onBlur={e => handleNameChange(e.target.value)} />
          <p style={{ fontSize: '0.72rem', color: PINK_WARN, marginTop: 4 }}>⚠️ Changing the name requires confirmation</p>
        </Field>

        <Field label="Category">
          <select className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
            value={form.category_id} onChange={e => set('category_id', e.target.value)}>
            <option value="">Select category...</option>
            {(isBulk ? bulkCats : assetCats).map(c => (
              <option key={c.category_id} value={c.category_id}>{c.name}</option>
            ))}
          </select>
        </Field>

        {!isBulk && (
          <>
            <Field label="Serial Number">
              <input className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.serial_number} onChange={e => set('serial_number', e.target.value)} />
            </Field>
            <Field label="Model Number">
              <input className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.model_number} onChange={e => set('model_number', e.target.value)} />
            </Field>
            <Field label="Battery Platform">
              <select className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.battery_platform} onChange={e => set('battery_platform', e.target.value)}>
                <option value="">Not applicable</option>
                <option>M18</option><option>M12</option>
                <option>20V MAX</option><option>FLEXVOLT</option>
                <option>18V</option><option>12V</option>
              </select>
            </Field>
            <Field label="Condition">
              <select className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.condition} onChange={e => set('condition', e.target.value)}>
                <option value="">Select...</option>
                <option value="new">New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="repair">Needs Repair</option>
                <option value="retired">Retired</option>
              </select>
            </Field>
            <Field label="Purchase Price">
              <input className="input" type="number" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} />
            </Field>
            <Field label="Purchase Date">
              <input className="input" type="date" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
            </Field>
            <Field label="Warranty Expiry">
              <input className="input" type="date" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.warranty_expiry} onChange={e => set('warranty_expiry', e.target.value)} />
            </Field>
          </>
        )}

        {isBulk && (
          <>
            <Field label="Unit of Measure">
              <select className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)}>
                <option value="">Select unit...</option>
                {['sheet','box','roll','each','lb','lf','sf','gallon','tube','bag','bundle'].map(u => (
                  <option key={u}>{u}</option>
                ))}
              </select>
            </Field>
            <Field label="Reorder Threshold">
              <input className="input" type="number" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.reorder_threshold} onChange={e => set('reorder_threshold', e.target.value)} />
            </Field>
            <Field label="Reorder Quantity">
              <input className="input" type="number" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={form.reorder_quantity} onChange={e => set('reorder_quantity', e.target.value)} />
            </Field>
            <div style={{ background: PINK_HEADER, border: `1px solid ${PINK_BORDER}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>
              <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#CC0000', marginBottom: 8 }}>
                📦 Current Stock: {item?.available_quantity} {item?.unit_of_measure || 'units'}
              </p>
              <button className="btn" style={{ background: PINK_WARN, color: 'white', minHeight: 44 }}
                onClick={() => setShowQtyAdjust(true)}>
                Adjust Quantity
              </button>
            </div>
          </>
        )}

        <Field label="Home Location">
          <select className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
            value={form.home_location_id} onChange={e => set('home_location_id', e.target.value)}>
            <option value="">Select location...</option>
            {locations.map(l => (
              <option key={l.location_id} value={l.location_id}>{l.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Manufacturer Website">
          <input className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
            value={form.manufacturer_website} onChange={e => set('manufacturer_website', e.target.value)} />
        </Field>

        <Field label="Manufacturer Phone">
          <input className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
            value={form.manufacturer_phone} onChange={e => set('manufacturer_phone', e.target.value)} />
        </Field>

        <Field label="Notes">
          <textarea className="input" rows={3} style={{ background: 'white', borderColor: PINK_BORDER, resize: 'none' }}
            value={form.notes} onChange={e => set('notes', e.target.value)} />
        </Field>

        <button className="btn" style={{ background: '#16A34A', color: 'white', marginTop: 16 }}
          onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : '💾 Save Changes'}
        </button>

        <button className="btn btn-ghost" style={{ marginTop: 10 }} onClick={() => navigate(`/item/${id}`)}>
          Cancel — Return to Item
        </button>

        <div style={{ marginTop: 24, borderTop: `2px solid ${PINK_BORDER}`, paddingTop: 16 }}>
          <p style={{ fontSize: '0.8rem', color: '#CC0000', marginBottom: 10, fontWeight: 600 }}>
            Danger Zone
          </p>
          <button className="btn" style={{ background: '#CC0000', color: 'white' }}
            onClick={() => setShowDeactivateConfirm(true)}>
            🗑️ Deactivate Item — Hide From Inventory
          </button>
        </div>

      </div>

      {/* NAME CONFIRMATION MODAL */}
      {showNameConfirm && (
        <div className="modal-overlay">
          <div className="modal-sheet" style={{ background: PINK_HEADER, borderTop: `3px solid ${PINK_WARN}` }}>
            <div className="modal-handle" />
            <h3 style={{ color: '#CC0000', marginBottom: 12 }}>⚠️ Confirm Name Change</h3>
            <p style={{ marginBottom: 8, fontSize: '0.9rem' }}>You are about to rename this item:</p>
            <p style={{ marginBottom: 4 }}><strong>From:</strong> {item?.name}</p>
            <p style={{ marginBottom: 16 }}><strong>To:</strong> {pendingName}</p>
            <p style={{ fontSize: '0.8rem', color: '#CC0000', marginBottom: 20 }}>
              This change is permanent. All future transactions will use the new name.
            </p>
            <button className="btn" style={{ background: '#CC0000', color: 'white', marginBottom: 10 }}
              onClick={confirmNameChange}>
              ✅ Yes, Rename Item
            </button>
            <button className="btn btn-ghost"
              onClick={() => { setShowNameConfirm(false); set('name', item?.name); }}>
              Cancel — Keep Original Name
            </button>
          </div>
        </div>
      )}

      {/* QUANTITY ADJUSTMENT MODAL */}
      {showQtyAdjust && (
        <div className="modal-overlay">
          <div className="modal-sheet" style={{ background: PINK_HEADER, borderTop: `3px solid ${PINK_WARN}` }}>
            <div className="modal-handle" />
            <h3 style={{ color: '#CC0000', marginBottom: 8 }}>Adjust Quantity</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 16 }}>
              Current: {item?.available_quantity} {item?.unit_of_measure || 'units'}
            </p>
            <Field label="New Quantity">
              <input className="input" type="number" style={{ background: 'white', borderColor: PINK_BORDER }}
                value={newQty} min={0} onChange={e => setNewQty(e.target.value)} />
            </Field>
            <Field label="Reason for Adjustment">
              <input className="input" style={{ background: 'white', borderColor: PINK_BORDER }}
                placeholder="e.g. Physical count correction, damaged stock..."
                value={qtyReason} onChange={e => setQtyReason(e.target.value)} />
            </Field>
            <button className="btn" style={{ background: '#CC0000', color: 'white', marginTop: 8, marginBottom: 10 }}
              onClick={handleQtyAdjust} disabled={saving}>
              {saving ? 'Saving...' : '✅ Confirm Adjustment'}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowQtyAdjust(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* PRINT LABEL MODAL */}
      {showPrintLabel && item && (
        <LabelPrintModal item={item} onClose={() => setShowPrintLabel(false)} />
      )}

      {/* DEACTIVATE CONFIRMATION MODAL */}
      {showDeactivateConfirm && (
        <div className="modal-overlay">
          <div className="modal-sheet" style={{ background: PINK_HEADER, borderTop: `3px solid ${PINK_WARN}` }}>
            <div className="modal-handle" />
            <h3 style={{ color: '#CC0000', marginBottom: 12 }}>⚠️ Deactivate Item</h3>
            <p style={{ marginBottom: 8, fontSize: '0.9rem' }}>
              This will hide <strong>{item?.name}</strong> from all inventory views.
            </p>
            <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: 20 }}>
              The item and its full transaction history are preserved in the database
              and can be restored by contacting your system administrator.
            </p>
            <button className="btn" style={{ background: '#CC0000', color: 'white', marginBottom: 10 }}
              onClick={handleDeactivate} disabled={saving}>
              {saving ? 'Deactivating...' : '🗑️ Yes, Deactivate Item'}
            </button>
            <button className="btn btn-ghost" onClick={() => setShowDeactivateConfirm(false)}>
              Cancel — Keep Item Active
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: '0.8rem', color: '#CC0000', display: 'block', marginBottom: 6, fontWeight: 600 }}>
        {label}
      </label>
      {children}
    </div>
  );
}